if (!customElements.get('inline-variant-selector')) {
  customElements.define(
    'inline-variant-selector',
    class InlineVariantSelector extends HTMLElement {
      constructor() {
        super();

        // Check if dataset exists and has required properties
        if (!this.dataset || !this.dataset.productId || !this.dataset.sectionId) {
          console.warn('InlineVariantSelector: missing required dataset properties');
          return;
        }

        this.productCard = this.closest('.card-wrapper');
        this.bagIcon = this.querySelector('.inline-variant-selector__bag-icon');
        this.variantPills = this.querySelector('.inline-variant-selector__pills');
        this.pillButtons = this.querySelectorAll('.inline-variant-selector__pill');
        this.productId = this.dataset.productId;
        this.sectionId = this.dataset.sectionId;
        this.isActive = false;
        this.inactivityTimeout = null;
        this.scrollY = 0;
        this.scrollThreshold = 15; // pixels

        if (this.bagIcon && this.variantPills) {
          this.setupEventListeners();
        }
        this.setupProductLinkFix();
      }

      setupEventListeners() {
        // Desktop: hover behavior on bag icon and pills area
        if (window.matchMedia('(min-width: 990px)').matches) {
          const handleMouseEnter = () => {
            this.showPills();
          };

          const handleMouseLeave = (e) => {
            // Only hide if we're actually leaving the component
            if (!this.contains(e.relatedTarget)) {
              this.hidePills();
            }
          };

          this.bagIcon.addEventListener('mouseenter', handleMouseEnter);
          this.variantPills.addEventListener('mouseenter', handleMouseEnter);
          this.addEventListener('mouseleave', handleMouseLeave);

          // Also add mouseleave to the product card to catch fast hovers
          if (this.productCard) {
            this.productCard.addEventListener('mouseleave', (e) => {
              // If the mouse leaves the entire card, always hide pills
              if (!this.productCard.contains(e.relatedTarget)) {
                this.hidePills();
              }
            });
          }
        } else {
          // Mobile: tap behavior
          this.bagIcon.addEventListener('click', this.togglePills.bind(this));
          document.addEventListener('click', this.handleOutsideClick.bind(this));
        }

        // Add scroll listener globally (for mobile usage)
        window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

        // Add to cart functionality for each pill
        this.pillButtons.forEach(pill => {
          if (!pill.disabled) {
            pill.addEventListener('click', this.handleAddToCart.bind(this));
          }
        });

        // Update on resize
        window.addEventListener('resize', this.handleResize.bind(this));
      }

      handleResize() {
        // Remove old listeners and re-setup based on new screen size
        const isDesktop = window.matchMedia('(min-width: 990px)').matches;

        if (isDesktop && !this._isDesktopMode) {
          this.hidePills();
          this._isDesktopMode = true;
        } else if (!isDesktop && this._isDesktopMode) {
          this.hidePills();
          this._isDesktopMode = false;
        }
      }

      showPills() {
        // Prevent retriggering if already active
        if (this.isActive) return;

        // Close any other open pills
        document.querySelectorAll('inline-variant-selector').forEach(selector => {
          if (selector !== this) {
            selector.hidePills();
          }
        });

        this.isActive = true;

        // Store current scroll position for mobile
        this.scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

        // Fade out bag icon and slide in pills
        this.bagIcon.style.opacity = '0';
        this.bagIcon.style.transform = 'scale(0.8)';

        setTimeout(() => {
          this.bagIcon.style.visibility = 'hidden';
          this.variantPills.classList.add('active');
        }, 100);

        // Start inactivity timeout on mobile
        this.startInactivityTimeout();
      }

      hidePills() {
        if (!this.isActive) return;

        this.isActive = false;

        // Clear inactivity timeout
        this.clearInactivityTimeout();

        // Immediately start showing bag icon (remove delay to prevent overlap)
        this.bagIcon.style.visibility = 'visible';
        this.bagIcon.style.opacity = '1';
        this.bagIcon.style.transform = 'scale(1)';
        this.bagIcon.classList.remove('hidden');

        // Slide out pills
        this.variantPills.classList.remove('active');
      }

      togglePills(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!window.matchMedia('(min-width: 990px)').matches) {
          // Close any other open pills
          document.querySelectorAll('inline-variant-selector').forEach(selector => {
            if (selector !== this && selector.isActive) {
              selector.hidePills();
            }
          });

          if (this.isActive) {
            this.hidePills();
          } else {
            // Show pills on mobile with smooth animation
            this.isActive = true;
            // Store current scroll position
            this.scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

            // Fade out bag icon smoothly (same as desktop)
            this.bagIcon.style.opacity = '0';
            this.bagIcon.style.transform = 'scale(0.8)';

            setTimeout(() => {
              this.bagIcon.style.visibility = 'hidden';
              this.variantPills.classList.add('active');
            }, 100);

            // Start inactivity timeout
            this.startInactivityTimeout();
          }
        }
      }

      handleOutsideClick(event) {
        if (!this.contains(event.target) && this.isActive) {
          this.hidePills();
        }
      }

      handleScroll(event) {
        // Only handle scroll on mobile when pills are active
        if (!this.isActive) return;
        if (window.matchMedia('(min-width: 990px)').matches) return;

        const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const scrollDelta = Math.abs(currentScrollY - this.scrollY);

        // Close pills if scrolled more than threshold
        if (scrollDelta > this.scrollThreshold) {
          this.hidePills();
        }
      }

      startInactivityTimeout() {
        // Clear any existing timeout
        this.clearInactivityTimeout();

        // Only set timeout on mobile
        if (window.matchMedia('(min-width: 990px)').matches) return;

        // Set 5 second timeout
        this.inactivityTimeout = setTimeout(() => {
          this.hidePills();
        }, 5000);
      }

      clearInactivityTimeout() {
        if (this.inactivityTimeout) {
          clearTimeout(this.inactivityTimeout);
          this.inactivityTimeout = null;
        }
      }

      async handleAddToCart(event) {
        event.preventDefault();
        event.stopPropagation();

        const pill = event.currentTarget;
        const variantId = pill.dataset.variantId;

        if (!variantId || pill.disabled) return;

        // Show loading state
        const originalText = pill.innerHTML;
        pill.classList.add('loading');
        pill.disabled = true;

        try {
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

          // Build form data with sections for bundled rendering
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', 1);

          // Request sections to be rendered with the cart update
          if (cart) {
            const sections = cart.getSectionsToRender().map((section) => section.id);
            formData.append('sections', sections.join(','));
            formData.append('sections_url', window.location.pathname);
          }

          // Add to cart via Shopify AJAX API with bundled section rendering
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData
          });

          const responseText = await response.text();

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          let responseData;
          try {
            responseData = JSON.parse(responseText);
          } catch (e) {
            throw new Error('Invalid response from server');
          }

          if (responseData.status) {
            throw new Error(responseData.description);
          }

          // Show success state
          pill.classList.remove('loading');
          pill.classList.add('success');
          pill.innerHTML = '<svg class="inline-variant-selector__checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Added';

          // Update cart sections using the bundled section rendering response
          if (cart && responseData.sections) {
            // Update each section with the rendered HTML from the response
            const sectionsToRender = cart.getSectionsToRender();

            sectionsToRender.forEach((section) => {
              const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);

              if (sectionElement && responseData.sections[section.id]) {
                sectionElement.innerHTML = this.getSectionInnerHTML(
                  responseData.sections[section.id],
                  section.selector
                );
              }
            });

            // Remove is-empty class if present
            const drawerInner = cart.querySelector('.drawer__inner');
            if (drawerInner && drawerInner.classList.contains('is-empty')) {
              drawerInner.classList.remove('is-empty');
            }
            if (cart.classList.contains('is-empty')) {
              cart.classList.remove('is-empty');
            }

            // Open cart drawer manually without triggering the problematic open() method
            setTimeout(() => {
              // Re-attach overlay click handler
              const overlay = cart.querySelector('#CartDrawer-Overlay');
              if (overlay) {
                // Remove old listeners by cloning
                const newOverlay = overlay.cloneNode(true);
                overlay.parentNode.replaceChild(newOverlay, overlay);
                // Add fresh listener
                newOverlay.addEventListener('click', cart.close.bind(cart));
              }

              // Manually add classes to open the drawer (bypassing the open() method)
              cart.classList.add('animate', 'active');
              document.body.classList.add('overflow-hidden');
            }, 50);
          }

          // Hide pills after a delay
          setTimeout(() => {
            this.hidePills();
            pill.classList.remove('success');
            pill.innerHTML = originalText;
            pill.disabled = false;
          }, 1500);

        } catch (error) {
          // Show error state
          pill.classList.remove('loading');
          pill.classList.add('error');
          pill.innerHTML = 'Error';

          setTimeout(() => {
            pill.classList.remove('error');
            pill.innerHTML = originalText;
            pill.disabled = false;
          }, 2000);
        }
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(selector).innerHTML;
      }

      setupProductLinkFix() {
        // Fix product card click navigation since overlay is disabled
        if (!this.productCard) return;

        const cardInner = this.productCard.querySelector('.card__inner');
        const productLink = this.productCard.querySelector('.card__heading a');

        if (cardInner && productLink) {
          cardInner.style.cursor = 'pointer';

          cardInner.addEventListener('click', (e) => {
            // Don't navigate if clicking on the variant selector, pills, or any interactive elements
            if (e.target.closest('.inline-variant-selector')) return;
            if (e.target.closest('button')) return;
            if (e.target.closest('a')) return;

            // Navigate to product page
            window.location.href = productLink.href;
          });
        }
      }
    }
  );
}
